import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useListPtoPprTkQuery,
  useUploadPtoPprTkMutation,
  useLazySearchPtoPprTkQuery,
  useDeletePtoPprTkMutation,
  useListPtoPprTkDraftsQuery,
  useCreatePtoPprTkDraftMutation,
  useLazyExportPtoPprTkDraftQuery,
  useDeletePtoPprTkDraftMutation,
  useUpdatePtoPprTkSectionMutation,
  useRegeneratePtoPprTkSectionMutation,
  type PtoPprTkDoc,
  type PtoPprTkChunk,
  type PtoPprTkDraft,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { EquipmentRegistry } from './ppr/EquipmentRegistry';
import { PprRdWizard } from './ppr/PprRdWizard';
import { RdDraftRow } from './ppr/RdDraftRow';
import { PRECEDENT_DOC_KINDS, docShort, isRdBusy } from './ppr/model';
import { AccentPanel } from './ppr/ui';

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 10px;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
`;

const DocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Hit = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
`;

const HitHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const HitBody = styled.div`
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Badge = styled.span<{ $color: string }>`
  display: inline-block;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 999px;
  color: #fff;
  background: ${({ $color }) => $color};
`;

const sourceMeta = (s: string): { label: string; color: string } => {
  if (s === 'precedent') return { label: 'прецедент', color: '#2e7d32' };
  if (s === 'engineer') return { label: 'за инженером', color: '#6a1b9a' };
  return { label: 'ИИ-черновик', color: '#b26a00' };
};

export function PtoPprTkLibrary() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');

  const { data: libData } = useListPtoPprTkQuery(projectId, { skip: !projectId });
  const [upload, { isLoading: uploading }] = useUploadPtoPprTkMutation();
  const [deleteDoc] = useDeletePtoPprTkMutation();
  const [search, { data: hitsData, isFetching: searching }] = useLazySearchPtoPprTkQuery();

  const docs: PtoPprTkDoc[] = libData?.items ?? [];
  const hits: PtoPprTkChunk[] = hitsData?.items ?? [];

  const [upName, setUpName] = useState('');
  const [upKind, setUpKind] = useState('ppr');
  const [upFile, setUpFile] = useState<File | null>(null);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [searchKind, setSearchKind] = useState('');
  const [didSearch, setDidSearch] = useState(false);

  // Пока черновик по РД в очереди/генерации/аудите — опрашиваем список раз в 3 с.
  const [pollDrafts, setPollDrafts] = useState(false);
  const { data: draftsData, refetch: refetchDrafts } = useListPtoPprTkDraftsQuery(projectId, {
    skip: !projectId,
    pollingInterval: pollDrafts ? 3000 : 0,
  });
  const [createDraft, { isLoading: creatingDraft }] = useCreatePtoPprTkDraftMutation();
  const [exportDraft] = useLazyExportPtoPprTkDraftQuery();
  const [deleteDraft] = useDeletePtoPprTkDraftMutation();
  const drafts: PtoPprTkDraft[] = draftsData?.items ?? [];
  // Черновики по РД (ИИ + аудитор) — своим списком над черновиками из прецедентов.
  const rdDrafts = drafts.filter((d) => d.mode === 'rd');
  const precedentDrafts = drafts.filter((d) => d.mode !== 'rd');
  const anyBusy = rdDrafts.some(isRdBusy);
  useEffect(() => setPollDrafts(anyBusy), [anyBusy]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [dfKind, setDfKind] = useState('ppr');
  const [dfTitle, setDfTitle] = useState('');
  const [dfWork, setDfWork] = useState('');
  const [openDraft, setOpenDraft] = useState('');
  const [updateSection] = useUpdatePtoPprTkSectionMutation();
  const [regenerateSection, { isLoading: regenerating }] = useRegeneratePtoPprTkSectionMutation();
  const [editKey, setEditKey] = useState('');
  const [editText, setEditText] = useState('');

  async function onUpload() {
    if (!upFile) return;
    setError('');
    const form = new FormData();
    form.append('name', upName.trim() || upFile.name);
    form.append('kind', upKind);
    form.append('file', upFile);
    try {
      await upload({ projectId, form }).unwrap();
      setUpName('');
      setUpFile(null);
    } catch {
      setError('Не удалось загрузить документ (проверьте формат: PDF/DOCX).');
    }
  }

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    setDidSearch(true);
    search({
      projectId,
      q: query.trim(),
      kind: searchKind || undefined,
      top_k: 10,
    });
  }

  function copyHit(text: string) {
    void navigator.clipboard?.writeText(text);
  }

  async function onCreateDraft() {
    if (!dfTitle.trim() || !dfWork.trim()) return;
    setError('');
    try {
      const res = await createDraft({
        projectId,
        kind: dfKind,
        title: dfTitle.trim(),
        work_type: dfWork.trim(),
      }).unwrap();
      setDfTitle('');
      setDfWork('');
      setOpenDraft(res.draft.id);
    } catch {
      setError('Не удалось собрать черновик (проверьте, что библиотека загружена).');
    }
  }

  async function onExportDraft(id: string) {
    try {
      const blob = await exportDraft({ id }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ppr_tk_draft_${id}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }

  async function onSaveSection(draftId: string, key: string) {
    try {
      await updateSection({ draftId, key, content: editText }).unwrap();
      setEditKey('');
      setEditText('');
    } catch {
      setError('Не удалось сохранить раздел.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Библиотека ППР/ТК</h1>
          <Muted>
            Прошлые ППР и технологические карты организации. Найдите прецедент и переиспользуйте
            формулировки — текст берётся из реальных документов.
          </Muted>
        </div>
        <Link to={`/agents/pto/${projectId}`}>
          <Button $variant="secondary">← К проекту</Button>
        </Link>
      </Row>

      {error && (
        <Panel style={{ borderColor: '#c0392b' }}>
          <Muted style={{ color: '#c0392b' }}>{error}</Muted>
        </Panel>
      )}

      {can_write && (
        <AccentPanel data-ppr="new-rd">
          <div>
            <h2>Новый ППР или ТК по РД (ИИ)</h2>
            <p>
              ИИ составит ППР, технологическую карту, ППР на высоте или ППР подъёмными сооружениями
              по выбранной РД проекта и технике; агент-аудитор проверит документ и добьётся
              исправлений. Документ появится в черновиках после утверждения аудитором.
            </p>
          </div>
          <Button $variant="primary" type="button" onClick={() => setWizardOpen(true)}>
            Новый ППР или ТК по РД (ИИ)
          </Button>
        </AccentPanel>
      )}
      {wizardOpen && (
        <PprRdWizard
          projectId={projectId}
          onClose={() => setWizardOpen(false)}
          onCreated={(id) => {
            setWizardOpen(false);
            setOpenDraft(id);
          }}
        />
      )}

      <Panel as="form" onSubmit={onSearch}>
        <Field>
          Поиск по прецедентам
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="монтаж металлоконструкций каркаса, контроль качества сварных швов…"
          />
        </Field>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Field style={{ marginBottom: 0 }}>
            Тип
            <Select value={searchKind} onChange={(e) => setSearchKind(e.target.value)}>
              <option value="">ППР и ТК</option>
              {PRECEDENT_DOC_KINDS.map(([code, short]) => (
                <option key={code} value={code}>
                  только {short}
                </option>
              ))}
            </Select>
          </Field>
          <Button $variant="primary" type="submit" disabled={!query.trim() || searching}>
            {searching ? 'Поиск…' : 'Найти'}
          </Button>
        </div>
      </Panel>

      {didSearch && (
        <Panel>
          <Muted style={{ marginBottom: 10 }}>
            {hits.length > 0 ? `Найдено фрагментов: ${hits.length}` : 'Ничего не найдено.'}
          </Muted>
          {hits.map((h) => (
            <Hit key={h.id}>
              <HitHead>
                <Muted>
                  <strong>
                    {docShort(h.doc_kind)}: {h.doc_name}
                  </strong>
                  {h.section_title ? ` · ${h.section_title}` : ''}
                  {h.page_number ? ` · с. ${h.page_number}` : ''}
                </Muted>
                <Button
                  $variant="ghost"
                  $size="sm"
                  type="button"
                  onClick={() => copyHit(h.content)}
                >
                  Копировать
                </Button>
              </HitHead>
              <HitBody>{h.content}</HitBody>
            </Hit>
          ))}
        </Panel>
      )}

      <Panel>
        <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Черновики ППР/ТК</h2>
        <Muted style={{ fontSize: 12, marginBottom: 8 }}>
          Сборка из прецедентов библиотеки + ИИ-черновик для отсутствующих разделов. Расчётные
          разделы (план, ТЭП, калькуляция) — за инженером. Стартовая точка для ручной доработки.
        </Muted>
        {can_write && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginBottom: 12,
            }}
          >
            <Field style={{ marginBottom: 0 }}>
              Тип
              <Select value={dfKind} onChange={(e) => setDfKind(e.target.value)}>
                {PRECEDENT_DOC_KINDS.map(([code, short]) => (
                  <option key={code} value={code}>
                    {short}
                  </option>
                ))}
              </Select>
            </Field>
            <Field style={{ marginBottom: 0, flex: 1, minWidth: 170 }}>
              Название
              <Input
                value={dfTitle}
                onChange={(e) => setDfTitle(e.target.value)}
                placeholder="ППР на монтаж каркаса"
              />
            </Field>
            <Field style={{ marginBottom: 0, flex: 1, minWidth: 170 }}>
              Вид работ
              <Input
                value={dfWork}
                onChange={(e) => setDfWork(e.target.value)}
                placeholder="монтаж металлоконструкций каркаса"
              />
            </Field>
            <Button
              $variant="primary"
              type="button"
              disabled={!dfTitle.trim() || !dfWork.trim() || creatingDraft}
              onClick={onCreateDraft}
            >
              {creatingDraft ? 'Сборка…' : 'Собрать черновик'}
            </Button>
          </div>
        )}
        {rdDrafts.map((d) => (
          <RdDraftRow
            key={d.id}
            draft={d}
            open={openDraft === d.id}
            onToggle={() => setOpenDraft(openDraft === d.id ? '' : d.id)}
            canWrite={can_write}
            onRefresh={refetchDrafts}
          />
        ))}
        {drafts.length === 0 ? (
          <Muted>Черновиков пока нет.</Muted>
        ) : (
          precedentDrafts.map((d) => (
            <div
              key={d.id}
              style={{ borderTop: '1px solid rgba(128,128,128,0.2)', padding: '8px 0' }}
            >
              <DocItem style={{ borderBottom: 'none', padding: 0 }}>
                <span style={{ flex: 1 }}>
                  <strong>{docShort(d.kind)}</strong> · {d.title}{' '}
                  <Muted as="span">({d.work_type})</Muted>
                </span>
                <Button
                  $variant="ghost"
                  $size="sm"
                  type="button"
                  onClick={() => setOpenDraft(openDraft === d.id ? '' : d.id)}
                >
                  {openDraft === d.id ? 'Свернуть' : 'Разделы'}
                </Button>
                <Button
                  $variant="secondary"
                  $size="sm"
                  type="button"
                  onClick={() => onExportDraft(d.id)}
                >
                  DOCX
                </Button>
                {can_write && (
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    onClick={() => deleteDraft(d.id)}
                  >
                    ✕
                  </Button>
                )}
              </DocItem>
              {openDraft === d.id &&
                d.sections.map((s) => {
                  const m = sourceMeta(s.source);
                  const editing = editKey === `${d.id}:${s.key}`;
                  return (
                    <div key={s.key} style={{ margin: '8px 0 8px 8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          marginBottom: 4,
                        }}
                      >
                        <strong style={{ fontSize: 14 }}>{s.title}</strong>
                        <Badge $color={m.color}>{m.label}</Badge>
                        <Badge $color={s.needs_review ? '#c0392b' : '#2e7d32'}>
                          {s.needs_review ? 'проверить' : 'проверено'}
                        </Badge>
                        {s.provenance.length > 0 && (
                          <Muted as="span">из: {s.provenance.join(', ')}</Muted>
                        )}
                        {can_write && !editing && (
                          <>
                            <Button
                              $variant="ghost"
                              $size="sm"
                              type="button"
                              onClick={() => {
                                setEditKey(`${d.id}:${s.key}`);
                                setEditText(s.content);
                              }}
                            >
                              Править
                            </Button>
                            {s.source !== 'engineer' && (
                              <Button
                                $variant="ghost"
                                $size="sm"
                                type="button"
                                disabled={regenerating}
                                onClick={() => regenerateSection({ draftId: d.id, key: s.key })}
                              >
                                Пересобрать
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                      {editing ? (
                        <div>
                          <TextArea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                          />
                          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <Button
                              $variant="primary"
                              $size="sm"
                              type="button"
                              onClick={() => onSaveSection(d.id, s.key)}
                            >
                              Сохранить
                            </Button>
                            <Button
                              $variant="ghost"
                              $size="sm"
                              type="button"
                              onClick={() => {
                                setEditKey('');
                                setEditText('');
                              }}
                            >
                              Отмена
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <HitBody style={{ fontSize: 13 }}>{s.content}</HitBody>
                      )}
                    </div>
                  );
                })}
            </div>
          ))
        )}
      </Panel>

      <Panel>
        <EquipmentRegistry canWrite={can_write} />
      </Panel>

      <Panel>
        <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Документы библиотеки</h2>
        {docs.length === 0 ? (
          <Muted>Пока пусто — загрузите прошлые ППР/ТК ниже.</Muted>
        ) : (
          docs.map((d) => (
            <DocItem key={d.id}>
              <span style={{ flex: 1 }}>
                <strong>{docShort(d.kind)}</strong> · {d.name}{' '}
                <Muted as="span">({d.total_chunks} фрагм.)</Muted>
              </span>
              {can_write && (
                <Button $variant="ghost" $size="sm" type="button" onClick={() => deleteDoc(d.id)}>
                  ✕
                </Button>
              )}
            </DocItem>
          ))
        )}
      </Panel>

      {can_write && (
        <Panel>
          <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Загрузить прецедент</h2>
          <Field>
            Название
            <Input
              value={upName}
              onChange={(e) => setUpName(e.target.value)}
              placeholder="ППР-001 ДГП-2 АО ПЭ-3000"
            />
          </Field>
          <Field>
            Тип
            <Select value={upKind} onChange={(e) => setUpKind(e.target.value)}>
              {PRECEDENT_DOC_KINDS.map(([code, , full]) => (
                <option key={code} value={code}>
                  {full}
                </option>
              ))}
            </Select>
          </Field>
          <input
            type="file"
            accept=".doc,.docx,.pdf"
            onChange={(e) => setUpFile(e.target.files?.[0] ?? null)}
          />
          <div style={{ marginTop: 8 }}>
            <Button
              $variant="primary"
              $size="sm"
              type="button"
              disabled={!upFile || uploading}
              onClick={onUpload}
            >
              {uploading ? 'Индексация…' : 'Загрузить и проиндексировать'}
            </Button>
          </div>
        </Panel>
      )}
    </div>
  );
}
